import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, CheckCircle, Sparkles, Shield, Zap } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';
import { getMsalInstance, microsoftLoginRequest } from '../../config/oauth';
import { post } from '../../lib/apiClient';
import { API_URL } from '../../_config';
import { generateIdempotencyKey } from '../../lib/idempotency';
import { decryptParams } from '../../lib/paramsCrypto';

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

const benefits = [
  { icon: Sparkles, title: "AI-Powered Discovery", desc: "Find verified leads from 210M+ contacts automatically" },
  { icon: Zap, title: "Smart Outreach", desc: "Generate personalized emails with AI that actually convert" },
  { icon: Shield, title: "Compliance Built-in", desc: "GDPR and CAN-SPAM compliant data sourcing" },
];

export default function SignupPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<'google' | 'microsoft' | null>(null);
  const { loginWithApiResponse } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [planParam,     setPlanParam]     = useState<string | undefined>(undefined);
  const [actionParam,   setActionParam]   = useState<string>('trial');
  const [intervalParam, setIntervalParam] = useState<'monthly' | 'yearly'>('monthly');
  const [refToken, setRefToken] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      decryptParams(token).then(params => {
        if (params) {
          setPlanParam(params.plan ?? undefined);
          setActionParam(params.action ?? 'trial');
          setIntervalParam((params.interval ?? 'monthly') as 'monthly' | 'yearly');
        }
      });
    } else {
      // Fallback: plain params (backward compatibility). Default to Silver trial when no plan is specified.
      setPlanParam(searchParams.get('plan') ?? 'silver');
      setActionParam(searchParams.get('action') ?? 'trial');
      setIntervalParam((searchParams.get('interval') ?? 'monthly') as 'monthly' | 'yearly');
    }

    const ref = searchParams.get('ref');
    if (ref) {
      setRefToken(ref);
      setInviteLoading(true);
      // VITE_API_BASE_URL already includes /api/v1/ — do not add v1/ again
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string).replace(/\/$/, '');
      fetch(`${apiBase}/auth/invite/${ref}`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.data) {
            setEmail(data.data.email);
            setFullName(data.data.name ?? '');
            setInviteError(null);
          } else {
            setInviteError('This invitation is invalid or has expired.');
          }
        })
        .catch(() => {
          setInviteError('This invitation is invalid or has expired.');
        })
        .finally(() => setInviteLoading(false));
    }
  }, [searchParams]);

  const getStrength = (pw: string) => {
    if (!pw) return { label: "", color: "", width: "0%" };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { label: "Weak", color: "var(--red)", width: "33%" };
    if (score <= 2) return { label: "Fair", color: "var(--amber)", width: "66%" };
    return { label: "Strong", color: "var(--green)", width: "100%" };
  };

  const strength = getStrength(password);

  const isValidEmail = (val: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!fullName || !email || !password) { setError('Please fill in all required fields'); return; }
    if (!isValidEmail(email)) { setEmailError('Please enter a valid email address.'); setError(''); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (!agreed) { setError('Please agree to the Terms and Privacy Policy'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await post<{
        access_token: string;
        refresh_token: string;
        token_type: string;
        expires_in: number;
        user: any;
        checkout_url?: string;
      }>(
        API_URL.AUTH.REGISTER,
        {
          name: fullName,
          email,
          password,
          password_confirmation: confirmPassword,
          ...(planParam ? { plan: planParam, action: actionParam, interval: intervalParam } : {}),
          ...(refToken ? { ref: refToken } : {}),
        },
        { headers: { 'Idempotency-Key': generateIdempotencyKey() } },
      );
      loginWithApiResponse(res.data as any);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        navigate('/app/dashboard');
      }
    } catch (err: any) {
      setError(err.message ?? 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (tokenResponse) => {
      if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) {
        setError('Google login is not configured yet. Add VITE_GOOGLE_CLIENT_ID to .env.local');
        return;
      }
      setSocialLoading('google');
      setError('');
      try {
        const body: Record<string, string> = { token: tokenResponse.access_token };
        if (planParam) { body.plan = planParam; body.action = actionParam; body.interval = intervalParam; }
        const res = await fetch(`${API_BASE}auth/social/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data?.error?.message ?? 'Google sign-up failed. Please try again.');
          return;
        }
        loginWithApiResponse(data.data);
        if (data.data?.checkout_url) {
          window.location.href = data.data.checkout_url;
        } else {
          navigate('/app/dashboard');
        }
      } catch {
        setError('Google sign-up failed. Please try again.');
      } finally {
        setSocialLoading(null);
      }
    },
    onError: () => {
      setError('Google sign-up was cancelled or failed.');
      setSocialLoading(null);
    },
  });

  const handleMicrosoftLogin = async () => {
    if (!import.meta.env.VITE_MICROSOFT_CLIENT_ID) {
      setError('Microsoft login is not configured yet. Add VITE_MICROSOFT_CLIENT_ID to .env.local');
      return;
    }
    setSocialLoading('microsoft');
    setError('');
    try {
      const msalInstance = await getMsalInstance();
      const tokenResponse = await msalInstance.loginPopup(microsoftLoginRequest);
      const body: Record<string, string> = { token: tokenResponse.accessToken };
      if (planParam) { body.plan = planParam; body.action = actionParam; body.interval = intervalParam; }
      const res = await fetch(`${API_BASE}auth/social/microsoft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error?.message ?? 'Microsoft sign-up failed. Please try again.');
        return;
      }
      loginWithApiResponse(data.data);
      if (data.data?.checkout_url) {
        window.location.href = data.data.checkout_url;
      } else {
        navigate('/app/dashboard');
      }
    } catch (err: any) {
      if (err?.errorCode !== 'user_cancelled') {
        setError('Microsoft sign-up failed. Please try again.');
      }
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <div className="su-wrap">
      <div className="su-left">
        <div className="su-left-dots" />
        <div className="su-left-glow" />
        <div className="su-left-inner">
          <Link to="/" className="su-logo-link">
            <img src="/images/logo.png" alt="LeadNova" className="su-logo" />
          </Link>
          <h1 className="su-left-title">Start closing more deals today</h1>
          <p className="su-left-desc">Join 2,000+ sales teams using LeadNova to supercharge their outbound pipeline.</p>

          <div className="su-benefits">
            {benefits.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="su-benefit">
                  <div className="su-benefit-icon">
                    <Icon size={20} color="#4DB8E8" />
                  </div>
                  <div>
                    <div className="su-benefit-title">{b.title}</div>
                    <div className="su-benefit-desc">{b.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="su-checks">
            {["14-day free trial, no credit card", "Cancel anytime, no commitments", "Full access to all features"].map(
              (t) => (
                <div key={t} className="su-check">
                  <CheckCircle size={16} color="#3AAA35" />
                  <span>{t}</span>
                </div>
              ),
            )}
          </div>
        </div>
      </div>

      <div className="su-right">
        {socialLoading !== null && (
          <div className="su-social-overlay">
            <div className="su-social-overlay-spinner" />
            <p className="su-social-overlay-title">
              Signing you up with {socialLoading === 'google' ? 'Google' : 'Microsoft'}…
            </p>
            <p className="su-social-overlay-sub">Please wait a moment</p>
          </div>
        )}
        <div className="su-right-inner">
          <div className="su-mobile-logo">
            <Link to="/">
              <img src="/images/logo.png" alt="LeadNova" className="su-logo-sm" />
            </Link>
          </div>
          <div className="su-form-header">
            <h2 className="su-form-title">Create your free account</h2>
            <p className="su-form-subtitle">14-day free trial. No credit card required.</p>
          </div>

          <div className="su-social-row">
            <button
              className="su-social-btn"
              onClick={() => handleGoogleLogin()}
              disabled={socialLoading !== null}
            >
              {socialLoading === 'google' ? (
                <span className="su-social-spinner" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              )}
              Google
            </button>
            <button
              className="su-social-btn"
              onClick={handleMicrosoftLogin}
              disabled={socialLoading !== null}
            >
              {socialLoading === 'microsoft' ? (
                <span className="su-social-spinner" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 23 23"><path fill="#f35325" d="M1 1h10v10H1z"/><path fill="#81bc06" d="M12 1h10v10H12z"/><path fill="#05a6f0" d="M1 12h10v10H1z"/><path fill="#ffba08" d="M12 12h10v10H12z"/></svg>
              )}
              Microsoft
            </button>
          </div>

          <div className="su-divider">
            <div className="su-divider-line" />
            <span className="su-divider-text">or continue with email</span>
            <div className="su-divider-line" />
          </div>

          {planParam && (
            <div className="su-plan-banner">
              {actionParam === 'purchase' ? `You're signing up for the ${planParam.charAt(0).toUpperCase() + planParam.slice(1)} plan — you'll be taken to checkout after account creation.` : `You're starting a 14-day free trial of the ${planParam.charAt(0).toUpperCase() + planParam.slice(1)} plan.`}
            </div>
          )}
          {error && <div className="su-error">{error}</div>}
          {inviteError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: '#dc2626' }}>
              {inviteError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="su-field">
              <label className="su-label">Full Name</label>
              <input className="su-input" type="text" placeholder="John Smith" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>

            <div className="su-field">
              <label className="su-label">Work Email</label>
              <input
                className={`su-input${emailError ? ' su-input-error' : ''}`}
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                onBlur={() => { if (email && !isValidEmail(email) && !(!!refToken && !inviteError)) setEmailError('Please enter a valid email address.'); }}
                readOnly={!!refToken && !inviteError}
              />
              {emailError && <p className="su-field-error">{emailError}</p>}
            </div>

            <div className="su-row">
              <div className="su-field">
                <label className="su-label">Password</label>
                <div className="su-input-wrap">
                  <input
                    className="su-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="su-eye-btn">
                    {showPassword ? (
                      <EyeOff size={18} color="var(--gray-500)" />
                    ) : (
                      <Eye size={18} color="var(--gray-500)" />
                    )}
                  </button>
                </div>
              </div>
              <div className="su-field">
                <label className="su-label">Confirm Password</label>
                <div className="su-input-wrap">
                  <input
                    className="su-input"
                    type={showConfirm ? "text" : "password"}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="su-eye-btn">
                    {showConfirm ? (
                      <EyeOff size={18} color="var(--gray-500)" />
                    ) : (
                      <Eye size={18} color="var(--gray-500)" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {password && (
              <div className="su-strength">
                <div className="su-strength-bars">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="su-strength-bar"
                      style={{
                        background:
                          i === 1
                            ? strength.color
                            : i === 2 && strength.width !== "33%"
                              ? strength.color
                              : i === 3 && strength.width === "100%"
                                ? strength.color
                                : "var(--gray-200)",
                      }}
                    />
                  ))}
                </div>
                <span className="su-strength-label" style={{ color: strength.color }}>
                  {strength.label}
                </span>
              </div>
            )}

            <label className="su-agree">
              <input type="checkbox" checked={agreed} onChange={() => setAgreed(!agreed)} />
              <span>
                I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>
              </span>
            </label>

            <button type="submit" className="su-submit-btn" disabled={loading || inviteLoading || !!inviteError}>
              {loading ? 'Creating account…' : <><span>Create Account</span><ArrowRight size={18} /></>}
            </button>
          </form>

          <p className="su-switch">
            Already have an account? <Link to="/login">Log in</Link>
          </p>
        </div>
      </div>

      <style>{`
        .su-wrap { min-height: calc(100vh - 72px); display: flex; }

        .su-left {
          flex: 0 0 45%; position: relative; overflow: hidden;
          background: linear-gradient(135deg, #1A2D5A 0%, #243A6C 40%, #1c3562 100%);
          display: flex; flex-direction: column; justify-content: center; padding: 60px 52px;
        }
        .su-left-dots {
          position: absolute; inset: 0; opacity: 0.05;
          background-image: radial-gradient(circle, #fff 1px, transparent 1px);
          background-size: 24px 24px;
        }
        .su-left-glow {
          position: absolute; bottom: -100px; right: -100px; width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(77,184,232,0.15), transparent 70%);
          pointer-events: none;
        }
        .su-left-inner { position: relative; z-index: 1; }

        .su-logo-link { display: inline-block; margin-bottom: 48px; }
        .su-logo { height: 56px; }
        .su-left-title { font-size: 36px; font-weight: 700; color: #fff; line-height: 1.2; margin-bottom: 12px; }
        .su-left-desc { font-size: 17px; color: rgba(255,255,255,0.65); line-height: 1.6; margin-bottom: 44px; max-width: 400px; }

        .su-benefits { display: flex; flex-direction: column; gap: 28px; margin-bottom: 48px; }
        .su-benefit { display: flex; gap: 16px; align-items: flex-start; }
        .su-benefit-icon {
          width: 44px; height: 44px; border-radius: 10px;
          background: rgba(77,184,232,0.15);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .su-benefit-title { font-size: 16px; font-weight: 600; color: #fff; margin-bottom: 4px; }
        .su-benefit-desc { font-size: 14px; color: rgba(255,255,255,0.55); line-height: 1.5; }

        .su-checks { display: flex; flex-direction: column; gap: 10px; }
        .su-check { display: flex; align-items: center; gap: 10px; font-size: 14px; color: rgba(255,255,255,0.7); }

        .su-right {
          flex: 0 0 55%; display: flex; align-items: center; justify-content: center;
          padding: 40px 24px; background: #fff; overflow-y: auto; position: relative;
        }
        .su-social-overlay {
          position: absolute; inset: 0; z-index: 10;
          background: rgba(255,255,255,0.97);
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 16px;
        }
        .su-social-overlay-spinner {
          width: 48px; height: 48px; border-radius: 50%;
          border: 3px solid var(--gray-200); border-top-color: var(--sky);
          animation: su-spin 0.7s linear infinite;
        }
        @keyframes su-spin { to { transform: rotate(360deg); } }
        .su-social-overlay-title { font-size: 18px; font-weight: 600; color: var(--navy); margin: 0; }
        .su-social-overlay-sub { font-size: 14px; color: var(--gray-500); margin: 0; }
        .su-right-inner { width: 100%; max-width: 480px; }
        .su-mobile-logo { display: none; text-align: center; margin-bottom: 24px; }
        .su-logo-sm { height: 44px; }

        .su-form-header { margin-bottom: 28px; }
        .su-form-title { font-size: 30px; font-weight: 700; color: var(--navy); margin-bottom: 8px; }
        .su-form-subtitle { font-size: 16px; color: var(--gray-500); }

        .su-social-row { display: flex; gap: 12px; margin-bottom: 24px; }
        .su-social-btn {
          flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
          padding: 12px 16px; border-radius: 10px; border: 1px solid var(--gray-200);
          background: #fff; font-size: 14px; font-weight: 500; color: var(--gray-900);
          cursor: pointer; font-family: inherit; transition: all 0.2s;
        }
        .su-social-btn:hover:not(:disabled) { background: var(--gray-50); }
        .su-social-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .su-social-spinner {
          width: 16px; height: 16px; border-radius: 50%;
          border: 2px solid var(--gray-300); border-top-color: var(--sky);
          animation: su-spin 0.6s linear infinite; flex-shrink: 0;
        }
        @keyframes su-spin { to { transform: rotate(360deg); } }

        .su-divider { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; }
        .su-divider-line { flex: 1; height: 1px; background: var(--gray-200); }
        .su-divider-text { font-size: 13px; color: var(--gray-500); font-weight: 500; white-space: nowrap; }

        .su-error { background: #FFEBEE; color: var(--red); padding: 12px 16px; border-radius: 8px; font-size: 14px; margin-bottom: 20px; }

        .su-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .su-field { margin-bottom: 20px; }
        .su-label { display: block; font-size: 14px; font-weight: 500; color: var(--gray-900); margin-bottom: 6px; }
        .su-input {
          width: 100%; height: 46px; padding: 0 14px; border-radius: 10px;
          border: 1px solid var(--gray-200); font-size: 15px; font-family: inherit;
          color: var(--gray-900); background: var(--gray-50); outline: none;
          transition: all 0.2s; box-sizing: border-box;
        }
        .su-input:focus { border-color: var(--sky); background: #fff; box-shadow: 0 0 0 3px rgba(77,184,232,0.1); }
        .su-input-error { border-color: var(--red) !important; }
        .su-input-error:focus { box-shadow: 0 0 0 3px rgba(239,68,68,0.1) !important; }
        .su-field-error { margin: 4px 0 0; font-size: 12px; color: var(--red); }
        .su-input-wrap { position: relative; }
        .su-input-wrap .su-input { padding-right: 44px; }
        .su-eye-btn { position: absolute; right: 14px; top: 13px; background: none; border: none; padding: 0; cursor: pointer; }

        .su-strength { margin-bottom: 16px; margin-top: -12px; }
        .su-strength-bars { display: flex; gap: 4px; margin-bottom: 4px; }
        .su-strength-bar { flex: 1; height: 3px; border-radius: 2px; transition: all 0.3s; }
        .su-strength-label { font-size: 12px; font-weight: 500; }

        .su-agree {
          display: flex; align-items: flex-start; gap: 10px; font-size: 14px;
          color: var(--gray-500); cursor: pointer; margin-bottom: 24px;
        }
        .su-agree input { width: 18px; height: 18px; margin-top: 1px; accent-color: var(--sky); flex-shrink: 0; }
        .su-agree a { color: var(--sky); text-decoration: none; }
        .su-agree a:hover { text-decoration: underline; }

        .su-submit-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; height: 48px; background: var(--green); color: #fff;
          border: none; border-radius: 10px; font-size: 16px; font-weight: 600;
          font-family: inherit; cursor: pointer;
          box-shadow: 0 4px 14px rgba(58,170,53,0.3); transition: all 0.2s;
          margin-bottom: 24px;
        }
        .su-submit-btn:hover:not(:disabled) { background: #2e9229; }
        .su-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .su-plan-banner { background: #E8F5E9; color: #2e7d32; padding: 12px 16px; border-radius: 8px; font-size: 14px; font-weight: 500; margin-bottom: 16px; }

        .su-switch { text-align: center; font-size: 15px; color: var(--gray-500); }
        .su-switch a { font-weight: 600; color: var(--sky); text-decoration: none; }
        .su-switch a:hover { text-decoration: underline; }

        @media (max-width: 768px) {
          .su-wrap { flex-direction: column; }
          .su-left { flex: none; padding: 32px 24px; }
          .su-left-title { font-size: 26px; }
          .su-left-desc { font-size: 15px; margin-bottom: 0; }
          .su-benefits { display: none; }
          .su-checks { display: none; }
          .su-logo-link { margin-bottom: 24px; }
          .su-logo { height: 44px; }
          .su-right { flex: none; padding: 32px 20px; }
        }

        @media (max-width: 480px) {
          .su-left { display: none; }
          .su-mobile-logo { display: block; }
          .su-right { min-height: calc(100vh - 72px); padding: 32px 20px; }
          .su-form-title { font-size: 26px; }
          .su-social-row { flex-direction: column; }
          .su-row { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
